import { Button, ButtonProps, IconButton, IconButtonProps } from "@mui/material";
import { MailOutline } from "@mui/icons-material";
import classNames from "classnames";
import React, { useState } from "react";
import { useButtonStyles } from "../../style/buttons.style";
import { useIconButtonsStyle } from "../../style/iconButtons.style";
import {
  AddIcon,
  ArrowRightIcon,
  CancelIcon,
  DeleteIcon,
  EditIcon,
  FilterIcon,
  IconColor,
  LoginAsIcon,
} from "./Icons";

interface IButtonsProps extends ButtonProps {
  isActive?: boolean;
  iconSvgColor?: IconColor;
  dataTestId?: string;
}

interface IFilterButtonProps extends IButtonsProps {
  isFilterActive?: boolean;
}

interface IIconButtonsProps extends IconButtonProps {
  isActive?: boolean;
  ref?: React.Ref<HTMLButtonElement>;
}

/*
 --- BUTTONS
*/
export const LoginAsButtonOutlined: React.FC<IButtonsProps> = ({
  children,
  onClick,
  disabled,
  variant = "outlined",
  ...props
}) => {
  return (
    <Button
      endIcon={<LoginAsIcon />}
      color="primary"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};

export const LoginButton: React.FC<IButtonsProps> = ({
  children,
  onClick,
  disabled,
  variant = "contained",
  ...props
}) => {
  return (
    <Button color="primary" variant={variant} disabled={disabled} onClick={onClick} style={{ fontSize: 16 }} {...props}>
      {children}
    </Button>
  );
};

export const CancelButton: React.FC<IButtonsProps> = ({
  children,
  onClick,
  disabled,
  variant = "contained",
  className,
  iconSvgColor,
  ...props
}) => {
  const { classes: { danger } } = useButtonStyles();
  return (
    <Button
      endIcon={<CancelIcon svgColor={iconSvgColor ? iconSvgColor : disabled ? "black" : "white"} />}
      color="secondary"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      className={`${className} ${danger}`}
      {...props}
    >
      {children}
    </Button>
  );
};

export const ForwardButton: React.FC<IButtonsProps> = ({
  children,
  onClick,
  disabled,
  variant = "contained",
  ...props
}) => {
  return (
    <Button
      endIcon={<ArrowRightIcon svgColor={variant === "outlined" ? "green" : "white"} />}
      color="primary"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};

export const FilterButton: React.FC<IFilterButtonProps> = ({
  children,
  onClick,
  disabled,
  variant = "outlined",
  isActive,
  isFilterActive,
  ...props
}) => {
  const [hasEntered, setHasEntered] = useState(false);
  return (
    <Button
      endIcon={<FilterIcon svgColor={hasEntered || isActive || isFilterActive ? "white" : "green"} />}
      onMouseEnter={() => setHasEntered(true)}
      onMouseLeave={() => setHasEntered(false)}
      color="primary"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      className={classNames({ "--active": isActive, "--filter-active": isFilterActive })}
      {...props}
    >
      {children}
    </Button>
  );
};

/*
 --- ICON BUTTONS
*/

export const DeleteIconButton: React.FC<IIconButtonsProps> = ({ onClick, disabled, ...props }) => {
  const { classes: { red, normal } } = useIconButtonsStyle();
  const [hasEntered, setHasEntered] = useState(false);
  const classes = [red, normal];

  return (
    <IconButton
      onMouseEnter={() => setHasEntered(true)}
      onMouseLeave={() => setHasEntered(false)}
      className={classes.join(" ")}
      color="primary"
      disabled={disabled}
      style={{ height: 35, width: 35 }}
      onClick={onClick}
      {...props}
    >
      <DeleteIcon svgColor={hasEntered ? "white" : "red"} />
    </IconButton>
  );
};

export const AddIconButton: React.FC<IIconButtonsProps> = ({ onClick, disabled, ...props }) => {
  const { classes: { green, normal } } = useIconButtonsStyle();
  const [hasEntered, setHasEntered] = useState(false);
  const classes = [green, normal];

  return (
    <IconButton
      onMouseEnter={() => setHasEntered(true)}
      onMouseLeave={() => setHasEntered(false)}
      className={classes.join(" ")}
      color="primary"
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      <AddIcon svgColor={hasEntered ? "white" : "green"} />
    </IconButton>
  );
};

export const EditIconButton: React.FC<IIconButtonsProps> = ({ onClick, disabled, ...props }) => {
  const { classes: { green, normal } } = useIconButtonsStyle();
  const [hasEntered, setHasEntered] = useState(false);
  const classes = [green, normal];

  return (
    <IconButton
      onMouseEnter={() => setHasEntered(true)}
      onMouseLeave={() => setHasEntered(false)}
      className={classes.join(" ")}
      color="primary"
      style={{ height: 35, width: 35 }}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      <EditIcon fontSize="small" svgColor={hasEntered ? "white" : "green"} />
    </IconButton>
  );
};

export const ArrowRightIconButton: React.FC<IIconButtonsProps> = ({
  onClick,
  disabled,
  ...props
}) => {
  const { classes: { green, normal } } = useIconButtonsStyle();
  const [hasEntered, setHasEntered] = useState(false);
  const classes = [green, normal];

  return (
    <IconButton
      onMouseEnter={() => setHasEntered(true)}
      onMouseLeave={() => setHasEntered(false)}
      className={classes.join(" ")}
      color="primary"
      disabled={disabled}
      onClick={onClick}
      style={{ height: 35, width: 35 }}
      {...props}
    >
      <ArrowRightIcon fontSize="small" svgColor={hasEntered ? "white" : "green"} />
    </IconButton>
  );
};

export const MailIconButton: React.FC<IButtonsProps> = ({
  children,
  onClick,
  disabled,
  variant = "contained",
  ...props
}) => {
  return (
    <Button
      endIcon={<MailOutline />}
      color="primary"
      variant={variant}
      disabled={disabled}
      onClick={onClick}
      {...props}
    >
      {children}
    </Button>
  );
};
